/**
 * mapUrlState — the concept map's drill-down state (which chapter is
 * exploded, which concept is at focus, which relation family is filtered,
 * whether the "tutte le relazioni" panel is open), mirrored into the page
 * URL's query string (task doc Phase 4 Core v1: "stato del drill-down
 * riflesso nell'URL (query param) così è condivisibile e il back del
 * browser funziona").
 *
 * Deliberately plain query params on the SAME `/read/:id/mappa` Inertia
 * route, manipulated via `window.history` directly (same technique
 * Tutor.tsx's `scrubDraftParam` already uses for its own `?draft=` param) —
 * NOT an Inertia `router.visit`/partial reload, which would re-render the
 * whole page from the server on every node click. This is pure client-side
 * navigation state; ConceptMap.tsx listens to `popstate` to stay in sync
 * with browser back/forward.
 *
 * Five params (TASK-020), all Italian keys like the two pre-existing ones,
 * hierarchically dependent in meaning (see `normalizeMapUrlState`):
 *   - `capitolo` — the macroId of the exploded chapter (meso level).
 *   - `concetto` — the nodeId at focus (fuoco) inside that chapter.
 *   - `famiglia` — the relation family filter chip (`structure`/`dynamic`/
 *     `contrast`); absent means `'all'` (default, never written to the URL).
 *   - `pannello` — `1` when the "Tutte le relazioni" panel is open on top of
 *     the current focus; meaningless (and dropped) without `concetto`.
 *   - `ripasso` — `1` when "Ripasso" (recall, labels hidden except focus) is
 *     active; chapter-scoped like `famiglia`, reset outside a chapter.
 */
import type { RelationFamily } from '@arianna/reader-core'

export interface MapUrlState {
  chapterId: string | null
  conceptId: string | null
  family: RelationFamily | 'all'
  panelOpen: boolean
  recall: boolean
}

export const MAP_ROOT_STATE: MapUrlState = {
  chapterId: null,
  conceptId: null,
  family: 'all',
  panelOpen: false,
  recall: false,
}

const FAMILIES: RelationFamily[] = ['structure', 'dynamic', 'contrast']

/** Drops dangling `concetto`/`pannello`/`famiglia` when their parent state
 *  is absent — never a reachable state through this module's own builders,
 *  but defends against a hand-edited/stale URL. */
export function normalizeMapUrlState(state: MapUrlState): MapUrlState {
  if (state.chapterId == null) {
    return { chapterId: null, conceptId: null, family: 'all', panelOpen: false, recall: false }
  }
  return {
    chapterId: state.chapterId,
    conceptId: state.conceptId,
    family: state.family,
    panelOpen: state.conceptId != null && state.panelOpen,
    recall: state.recall,
  }
}

export function parseMapUrlState(search: string): MapUrlState {
  const params = new URLSearchParams(search)
  const rawFamily = params.get('famiglia')
  const family: RelationFamily | 'all' =
    rawFamily != null && (FAMILIES as string[]).includes(rawFamily)
      ? (rawFamily as RelationFamily)
      : 'all'
  return normalizeMapUrlState({
    chapterId: params.get('capitolo'),
    conceptId: params.get('concetto'),
    family,
    panelOpen: params.get('pannello') === '1',
    recall: params.get('ripasso') === '1',
  })
}

/** Query string ONLY (e.g. `?capitolo=1&concetto=1.a`), `''` at the root
 *  state — callers combine it with `window.location.pathname`. */
export function buildMapSearch(state: MapUrlState): string {
  const normalized = normalizeMapUrlState(state)
  const params = new URLSearchParams()
  if (normalized.chapterId != null) params.set('capitolo', normalized.chapterId)
  if (normalized.conceptId != null) params.set('concetto', normalized.conceptId)
  if (normalized.family !== 'all') params.set('famiglia', normalized.family)
  if (normalized.panelOpen) params.set('pannello', '1')
  if (normalized.recall) params.set('ripasso', '1')
  const qs = params.toString()
  return qs.length > 0 ? `?${qs}` : ''
}

export function mapUrlStatesEqual(a: MapUrlState, b: MapUrlState): boolean {
  return (
    a.chapterId === b.chapterId &&
    a.conceptId === b.conceptId &&
    a.family === b.family &&
    a.panelOpen === b.panelOpen &&
    a.recall === b.recall
  )
}
